import React, { useState } from 'react';
import { clsx } from 'clsx';
import { DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';

type KnowledgeMode = 'strict' | 'augmented';

interface KnowledgeModeCardsProps {
  mode: KnowledgeMode;
  onChange: (mode: KnowledgeMode) => void;
  disabled?: boolean;
}

/**
 * 'strict' and 'augmented' are API values and stay that way — they are not
 * words a customer should have to interpret.
 *
 * Deliberately NOT gated on having documents. converse handles strict-with-no-KB
 * explicitly (it answers "I don't have any documentation configured yet"), and
 * choosing this before uploading is a reasonable thing to want during setup.
 */
const CHOICES: Array<{
  value: KnowledgeMode;
  label: string;
  description: string;
  bestFor: string;
  icon: typeof DocumentTextIcon;
}> = [
  // 'strict' is the default for new chatbots, so it leads (left).
  {
    value: 'strict',
    label: 'Documents only',
    description:
      "Answers strictly from your content. If it isn't in your knowledge base, the chatbot says it doesn't know.",
    bestFor: 'Best for support, policy and compliance.',
    icon: DocumentTextIcon,
  },
  {
    value: 'augmented',
    label: 'Documents first',
    description:
      "Prefers your content, and falls back on general knowledge when your documents don't cover the question.",
    bestFor: 'Best for general-purpose assistants.',
    icon: SparklesIcon,
  },
];

/**
 * C3: collapsed to one line by default, expanding to the cards on demand.
 *
 * This is a binary most people set once and never revisit, and as two large cards with
 * a title, a description and a "best for" line each it took roughly a third of the
 * Knowledge tab — permanently, for everyone, to display a setting that had already been
 * decided. Collapsed it costs one line; the full explanation is still one click away for
 * the person actually making the choice, which is why the cards are kept rather than
 * replaced with a bare toggle.
 *
 * Auto-expands when nothing is set yet, so a genuinely undecided user is not asked to
 * discover the control first.
 */
export default function KnowledgeModeCards({ mode, onChange, disabled }: KnowledgeModeCardsProps) {
  const [expanded, setExpanded] = useState(!mode);
  const current = CHOICES.find(c => c.value === mode);

  if (!expanded) {
    return (
      <div className="flex items-baseline gap-2 text-sm">
        <span className="text-gray-500 dark:text-gray-400">Answering behaviour:</span>
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {current?.label ?? 'Not set'}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          disabled={disabled}
          className="text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:no-underline"
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Answering behaviour</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">
        What your chatbot is allowed to draw on when it replies.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CHOICES.map(choice => {
          const selected = mode === choice.value;
          const Icon = choice.icon;
          return (
            <label
              key={choice.value}
              className={clsx(
                'flex gap-3 p-4 rounded-xl border transition-colors',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                selected
                  ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-slate-700/50'
                  : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
              )}
            >
              <input
                type="radio"
                name="knowledge-mode"
                checked={selected}
                disabled={disabled}
                onChange={() => {
                  onChange(choice.value);
                  // Collapse on selection: the cards exist to inform the decision, and
                  // once it is made they are back to occupying a third of the tab.
                  setExpanded(false);
                }}
                className="mt-0.5 h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{choice.label}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{choice.description}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">{choice.bestFor}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
